/**
 * themeService.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * src/shared/services/themeService.ts
 *
 * Manages user theme preferences end-to-end:
 *  1. Applies preferences to the DOM (data attributes + CSS custom properties)
 *  2. Persists preferences to Supabase (profiles.theme_preferences jsonb column)
 *  3. Loads preferences on app start and re-applies them
 *
 * Theme model (ver2 — April 2026):
 *  · Default = Warm Sand (CSS defaults, no overrides needed)
 *  · Custom  = user picks via colour wheel + lightness slider
 *
 * The colour wheel derives an entire palette from a single hex value.
 * The lightness slider naturally creates dark mode when pushed below 50%.
 * No separate dark mode toggle needed.
 *
 * RULE: Never call raw DOM setAttribute/setProperty for theming in components.
 * Always call the exported functions from this service.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { supabase } from './supabase';

// ─── Types ──────────────────────────────────────────────────────────────────

export type ThemePreset =
  | 'default'        // Warm Sand — CSS defaults, no overrides
  | 'custom';        // User-selected custom colour via wheel

export type TextSize =
  | 'small'
  | 'medium'   // default — no attribute needed
  | 'large';

export interface ThemePrefs {
  preset:        ThemePreset;
  textSize:      TextSize;
  reduceMotion:  boolean;
  customColour?: string;  // Hex colour when preset === 'custom'
}

// The shape stored in profiles.theme_preferences (jsonb)
interface StoredThemePrefs {
  preset?:        ThemePreset;
  textSize?:      TextSize;
  reduceMotion?:  boolean;
  customColour?:  string;
}

// ─── Defaults ───────────────────────────────────────────────────────────────

const DEFAULT_PREFS: ThemePrefs = {
  preset:       'default',
  textSize:     'medium',
  reduceMotion: false,
};

// ─── Colour Utilities ───────────────────────────────────────────────────────

/**
 * Convert hex to HSL values
 */
function hexToHsl(hex: string): { h: number; s: number; l: number } {
  hex = hex.replace(/^#/, '');

  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * Generate HSL string
 */
function hsl(h: number, s: number, l: number): string {
  return `hsl(${h}, ${s}%, ${l}%)`;
}

// ─── DOM Application ─────────────────────────────────────────────────────────

/**
 * applyTheme()
 *
 * Sets data-* attributes on the <html> element for the default theme,
 * or applies CSS custom properties directly for custom colours.
 */
export function applyTheme(prefs: ThemePrefs): void {
  const html = document.documentElement;

  // ── Handle custom colour ──
  if (prefs.preset === 'custom' && prefs.customColour) {
    html.removeAttribute('data-theme');
    applyCustomColour(prefs.customColour);
  } else {
    // Default — clear everything and let CSS defaults take over
    clearCustomColour();
    html.removeAttribute('data-theme');
  }

  // ── data-text-size ──
  if (prefs.textSize === 'medium') {
    html.removeAttribute('data-text-size');
  } else {
    html.setAttribute('data-text-size', prefs.textSize);
  }

  // ── data-reduce-motion ──
  if (prefs.reduceMotion) {
    html.setAttribute('data-reduce-motion', 'true');
  } else {
    html.removeAttribute('data-reduce-motion');
  }
}

/**
 * applyCustomColour()
 *
 * Derives a full theme from a single hex colour and applies CSS variables.
 * The colour becomes the background; all other colours are calculated.
 */
export function applyCustomColour(hex: string): void {
  const html = document.documentElement;
  const { h, s, l } = hexToHsl(hex);

  // Determine if this is a dark theme (lightness below 50)
  const isDark = l < 50;

  // Apply the derived theme
  html.style.setProperty('--ui-bg', hsl(h, s, l));
  html.style.setProperty('--ui-card', hsl(h, s, Math.min(98, l + (isDark ? 6 : 4))));
  html.style.setProperty('--ui-page-bg', hsl(h, s, Math.max(5, l - 8)));

  // Text colours — three tiers based on background lightness
  // Dark backgrounds need light text, medium backgrounds need high-contrast
  // dark text, very light backgrounds can use softer muted tones.
  if (isDark) {
    // Dark theme: light text
    html.style.setProperty('--ui-text', '#F5F5F5');
    html.style.setProperty('--ui-text-muted', '#A0A5AA');
    html.style.setProperty('--ui-text-light', '#707580');
  } else if (l < 80) {
    // Medium-tone background: needs stronger contrast on muted text
    html.style.setProperty('--ui-text', '#000000');
    html.style.setProperty('--ui-text-muted', '#2C2825');
    html.style.setProperty('--ui-text-light', '#44403C');
  } else {
    // Very light background: standard muted tones
    html.style.setProperty('--ui-text', '#000000');
    html.style.setProperty('--ui-text-muted', '#57534E');
    html.style.setProperty('--ui-text-light', '#78716C');
  }

  // Border colour — slightly darker/lighter than background
  html.style.setProperty('--ui-border', hsl(h, s * 0.8, isDark ? l + 12 : l - 8));

  // Toggle colours for dark themes
  if (isDark) {
    html.style.setProperty('--ui-toggle-off', '#44484F');
    html.style.setProperty('--ui-toggle-thumb', '#FFFFFF');
  }
}

/**
 * clearCustomColour()
 *
 * Removes inline CSS custom properties so CSS defaults take over.
 */
function clearCustomColour(): void {
  const html = document.documentElement;
  const props = [
    '--ui-bg', '--ui-card', '--ui-page-bg',
    '--ui-text', '--ui-text-muted', '--ui-text-light',
    '--ui-border', '--ui-toggle-off', '--ui-toggle-thumb'
  ];
  props.forEach(prop => html.style.removeProperty(prop));
}

/**
 * resetTheme()
 *
 * Removes all theme attributes and custom properties, restoring CSS defaults.
 * Does NOT save to Supabase — use saveThemePrefs for that.
 */
export function resetTheme(): void {
  const html = document.documentElement;
  html.removeAttribute('data-theme');
  html.removeAttribute('data-text-size');
  html.removeAttribute('data-reduce-motion');
  clearCustomColour();
}

// ─── Supabase Persistence ────────────────────────────────────────────────────

/**
 * saveThemePrefs()
 *
 * Persists the user's preferences to profiles.theme_preferences (jsonb).
 * Also applies them to the DOM immediately so the user sees the change
 * without waiting for the async write to complete.
 */
export async function saveThemePrefs(
  profileId: string,
  prefs: ThemePrefs
): Promise<void> {
  // Apply immediately — don't make the user wait for the DB round-trip
  applyTheme(prefs);

  const stored: StoredThemePrefs = {
    preset:       prefs.preset,
    textSize:     prefs.textSize,
    reduceMotion: prefs.reduceMotion,
  };

  // Only store customColour if it's set
  if (prefs.customColour) {
    stored.customColour = prefs.customColour;
  }

  const { error } = await supabase
    .from('profiles')
    .update({ theme_preferences: stored })
    .eq('id', profileId);

  if (error) {
    console.error('[themeService] Failed to save theme preferences:', error.message);
  }
}

/**
 * loadThemePrefs()
 *
 * Reads theme preferences from Supabase and applies them.
 * Call this once after the user authenticates.
 */
export async function loadThemePrefs(profileId: string): Promise<ThemePrefs> {
  const { data, error } = await supabase
    .from('profiles')
    .select('theme_preferences')
    .eq('id', profileId)
    .single();

  if (error || !data) {
    console.warn('[themeService] Could not load theme preferences, using defaults.');
    applyTheme(DEFAULT_PREFS);
    return DEFAULT_PREFS;
  }

  const stored = (data.theme_preferences ?? {}) as StoredThemePrefs;

  // Graceful migration: if someone had an old preset (sunset, forest, etc.)
  // treat it as custom if they had a customColour, otherwise default
  let preset: ThemePreset = 'default';
  if (stored.preset === 'custom') {
    preset = 'custom';
  } else if (stored.preset && stored.preset !== 'default' && stored.customColour) {
    // Old preset that no longer exists — migrate to custom
    preset = 'custom';
  }

  const prefs: ThemePrefs = {
    preset,
    textSize:     stored.textSize     ?? DEFAULT_PREFS.textSize,
    reduceMotion: stored.reduceMotion ?? DEFAULT_PREFS.reduceMotion,
    customColour: stored.customColour,
  };

  applyTheme(prefs);
  return prefs;
}

/**
 * getDefaultPrefs()
 *
 * Returns a copy of the default preferences.
 */
export function getDefaultPrefs(): ThemePrefs {
  return { ...DEFAULT_PREFS };
}

// ─── Human-readable labels (used in Settings UI) ────────────────────────────

export const THEME_LABELS: Record<ThemePreset, string> = {
  default: 'Warm Sand',
  custom:  'Custom',
};
